/**
 * Validation utilities for form inputs
 * Centralized validation logic to avoid duplication
 */

/**
 * Email validation regex pattern
 * Matches standard email format: user@domain.com
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates if a string is a valid email address
 * @param email - The email string to validate
 * @returns true if email is valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
    if (!email || typeof email !== 'string') {
        return false;
    }
    return EMAIL_REGEX.test(email.trim());
};

/**
 * Formats Emirates ID number according to UAE standard format: XXX-XXXX-XXXXXXX-X
 * Removes all non-numeric characters, then formats as XXX-XXXX-XXXXXXX-X
 * @param value - The input string (can contain numbers, dashes, or other characters)
 * @returns Formatted Emirates ID string (XXX-XXXX-XXXXXXX-X) or empty string
 */
export const formatEmiratesId = (value: string): string => {
    if (!value) return '';

    // Remove all non-numeric characters
    const digitsOnly = value.replace(/\D/g, '');

    // Limit to 15 digits (standard Emirates ID length)
    const limitedDigits = digitsOnly.slice(0, 15);

    // Format as XXX-XXXX-XXXXXXX-X
    if (limitedDigits.length <= 3) {
        return limitedDigits;
    } else if (limitedDigits.length <= 7) {
        return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
    } else if (limitedDigits.length <= 14) {
        return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 7)}-${limitedDigits.slice(7)}`;
    } else {
        return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 7)}-${limitedDigits.slice(7, 14)}-${limitedDigits.slice(14)}`;
    }
};

/**
 * Removes formatting from Emirates ID (removes dashes)
 * @param formattedId - The formatted Emirates ID string
 * @returns Unformatted Emirates ID (digits only)
 */
export const unformatEmiratesId = (formattedId: string): string => {
    if (!formattedId) return '';
    return formattedId.replace(/\D/g, '');
};

/**
 * Validates if a name field is valid (non-empty and trimmed)
 * @param name - The name string to validate
 * @returns Error message if invalid, empty string if valid
 */
export const validateName = (name: string): string => {
    if (!name || typeof name !== 'string') {
        return 'This field is required';
    }
    const trimmed = name.trim();
    if (trimmed.length === 0) {
        return 'This field is required';
    }
    if (trimmed.length < 2) {
        return 'Name must be at least 2 characters';
    }
    // Allow letters, spaces, hyphens, and apostrophes for names
    if (!/^[a-zA-Z\s'-]+$/.test(trimmed)) {
        return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }
    return '';
};

/**
 * Validates if an optional name field is valid (can be empty, but if provided must be valid)
 * @param name - The name string to validate
 * @returns Error message if invalid, empty string if valid
 */
export const validateOptionalName = (name: string): string => {
    if (!name || typeof name !== 'string') {
        return ''; // Empty is valid for optional fields
    }
    const trimmed = name.trim();
    if (trimmed.length === 0) {
        return ''; // Empty is valid for optional fields
    }
    if (trimmed.length < 2) {
        return 'Name must be at least 2 characters';
    }
    // Allow letters, spaces, hyphens, and apostrophes for names
    if (!/^[a-zA-Z\s'-]+$/.test(trimmed)) {
        return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }
    return '';
};

/**
Key Components of the Regex
^784-? : Matches the initial 784 (the UAE's ISO country code), with an optional hyphen (-?).
[0-9]{4}-? : Matches the next four digits (often the year of birth), with an optional hyphen.
[0-9]{7}-? : Matches the next seven digits (a unique random number), with an optional hyphen.
[0-9]{1}$ : Matches the final single check digit, at the end of the string ($).
 */
const EMIRATES_ID_REGEX = /^784-?[0-9]{4}-?[0-9]{7}-?[0-9]{1}$/;

/**
 * Validates Emirates ID format
 * @param emiratesId - The Emirates ID string to validate (can be formatted or unformatted)
 * @returns Error message if invalid, empty string if valid
 */
export const validateEmiratesId = (emiratesId: string): string => {
    if (!emiratesId || typeof emiratesId !== 'string') {
        return 'Emirates ID is required';
    }
    const trimmed = emiratesId.trim();
    if (trimmed.length === 0) {
        return 'Emirates ID is required';
    }
    
    // Remove dashes for digit count check
    const digitsOnly = trimmed.replace(/\D/g, '');
    
    // Check if we have exactly 15 digits (standard Emirates ID length)
    if (digitsOnly.length !== 15) {
        return 'Emirates ID must be 15 digits';
    }
    
    // Validate format using regex (supports both standard 784-YYYY-NNNNNNN-X and legacy XXX-XXXX-XXXXXXX-X)
    if (!EMIRATES_ID_REGEX.test(trimmed)) {
        return 'Invalid Emirates ID format';
    }
    
    // // Additional validation: If using standard format (starts with 784), validate year range
    // if (digitsOnly.startsWith('784')) {
    //     const year = parseInt(digitsOnly.substring(3, 7), 10);
    //     const currentYear = new Date().getFullYear();
    //     // Year should be between 1900 and current year + 1 (for future issuance)
    //     if (year < 1900 || year > currentYear + 1) {
    //         return 'Invalid Emirates ID year';
    //     }
    // }
    
    return '';
};

/**
 * Passport number validation regex pattern
 * Validates alphanumeric characters (letters and numbers only, case-insensitive)
 * Pattern: ^[A-Za-z0-9]+$ - Allows any combination of letters and numbers
 * Examples: A1234567, AB12345678, P123456789, 123456789, ABC123456
 * 
 * Requirements:
 * - Minimum 6 characters
 * - Maximum 12 characters
 * - Alphanumeric only (letters and numbers)
 */
const PASSPORT_NUMBER_REGEX = /^[A-Za-z0-9]+$/;

/**
 * Validates Passport Number
 * @param passportNumber - The passport number string to validate
 * @returns Error message if invalid, empty string if valid
 */
export const validatePassportNumber = (passportNumber: string): string => {
    if (!passportNumber || typeof passportNumber !== 'string') {
        return 'Passport number is required';
    }
    const trimmed = passportNumber.trim();
    if (trimmed.length === 0) {
        return 'Passport number is required';
    }
    
    // Length validation: Passport must be between 6-12 characters
    if (trimmed.length < 6) {
        return 'Passport must be at least 6 characters';
    }
    if (trimmed.length > 12) {
        return 'Passport must be at most 12 characters';
    }
    
    // Validate format using regex: alphanumeric only (letters and numbers)
    // Pattern: ^[A-Za-z0-9]+$ - Allows any combination of letters and numbers
    if (!PASSPORT_NUMBER_REGEX.test(trimmed)) {
        return 'Passport must be alphanumeric';
    }
    
    return '';
};

/**
 * Phone number validation constants
 * Min length: 6 digits
 * Max length: 15 digits (international standard)
 */
export const PHONE_NUMBER_MIN_LENGTH = 6;
export const PHONE_NUMBER_MAX_LENGTH = 15;

/**
 * Validates Phone Number
 * @param phoneNumber - The phone number string to validate (without country code)
 * @returns Error message if invalid, empty string if valid
 */
export const validatePhoneNumber = (phoneNumber: string): string => {
    if (!phoneNumber || typeof phoneNumber !== 'string') {
        return 'Phone number is required';
    }
    const trimmed = phoneNumber.trim();
    if (trimmed.length === 0) {
        return 'Phone number is required';
    }
    // Remove spaces and common formatting characters
    const digitsOnly = trimmed.replace(/[\s\-\(\)]/g, '');
    // Phone number should be between 6 and 15 digits
    if (digitsOnly.length < PHONE_NUMBER_MIN_LENGTH) {
        return `Phone number must be at least ${PHONE_NUMBER_MIN_LENGTH} digits`;
    }
    if (digitsOnly.length > PHONE_NUMBER_MAX_LENGTH) {
        return `Phone number must be at most ${PHONE_NUMBER_MAX_LENGTH} digits`;
    }
    // Should only contain digits
    if (!/^\d+$/.test(digitsOnly)) {
        return 'Phone number can only contain numbers';
    }
    return '';
};

/**
 * Formats phone number input to enforce max length
 * Removes non-digit characters and limits to max length
 * @param value - The input string
 * @returns Formatted phone number string (digits only, max 15 characters)
 */
export const formatPhoneNumberInput = (value: string): string => {
    if (!value || typeof value !== 'string') {
        return '';
    }
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    // Limit to max length
    return digitsOnly.slice(0, PHONE_NUMBER_MAX_LENGTH);
};

